// In-app language switcher. Lives in the sidebar footer and on most
// long-form routes. Switching language updates:
//   1. i18next active language
//   2. <html lang> + dir attributes
//   3. localStorage marker
//   4. URL — re-prefixes the current path with the new language code
//      (English drops the prefix entirely; other languages get /xx/ prefix)
//
// Uses a small native <select> rather than a full dropdown component to
// keep the bundle lean and stay accessible on every device.

import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { SUPPORTED_LANGS, localizedPath, stripLangPrefix, persistLanguage, type SupportedLang } from '../i18n';

interface LangMeta { code: SupportedLang; native: string }

const LANG_META: LangMeta[] = [
  { code: 'en', native: 'English' },
  { code: 'es', native: 'Español' },
  { code: 'pt', native: 'Português' },
  { code: 'ar', native: 'العربية' },
  { code: 'de', native: 'Deutsch' },
  { code: 'fr', native: 'Français' },
  { code: 'sv', native: 'Svenska' },
  { code: 'ja', native: '日本語' },
  { code: 'ms', native: 'Bahasa Melayu' },
];

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { i18n, t } = useTranslation();
  const [location] = useLocation();
  const current = i18n.language as SupportedLang;

  const onChange = (next: SupportedLang) => {
    if (next === current) return;
    // Hard-navigate rather than wouter's client-side navigate: the Router's
    // `base` is derived from the URL prefix at mount time, so a soft nav to
    // a different-language URL leaves the base stale and every inner route
    // fails to match → <NotFound />. A full reload remounts with the
    // correct base and the locale bundle picks up from the new URL.
    persistLanguage(next);
    const stripped = stripLangPrefix(location);
    window.location.assign(localizedPath(stripped, next));
  };

  return (
    <label className={compact
      ? 'inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-text-2)]'
      : 'inline-flex items-center gap-2 text-[12px] text-[var(--color-text-2)]'
    }>
      <span className="text-[var(--color-text-3)]">{t('lang.switch_label')}</span>
      <select
        value={current}
        onChange={(e) => onChange(e.target.value as SupportedLang)}
        aria-label={t('lang.switch_label')}
        className="rounded border border-[var(--color-line)] bg-white px-1.5 py-0.5 text-[11.5px] font-medium text-[var(--color-text-2)] hover:border-[var(--color-line-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30"
      >
        {LANG_META.filter(l => SUPPORTED_LANGS.includes(l.code)).map((l) => (
          <option key={l.code} value={l.code}>{l.native}</option>
        ))}
      </select>
    </label>
  );
}
